import React, { useMemo } from 'react';
import { Button } from '@99designs/frontend-components/components/Button';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import Box from '../../../../shared/spa/core-components/Box';
import { webOnboarderGetStartedPath } from '../../../../shared/lib/platformUrls';
import { colors } from '../../../../shared/lib/styles';
import Columns, { Column } from '../../../../shared/components/Columns';
import CtaPair from '../../../../shared/components/CtaPair';
import Heading from '../../../../shared/components/Heading';
import Paragraph from '../../../../shared/components/Paragraph';
import Category, { CategoryType } from './Category';

const imageFolder = 'website-project';

function getCategories(languageCode: string): CategoryType[] {
    languageCode = languageCode === 'fr' ? 'fr' : 'en';
    return [
        {
            id: 'ecommerce',
            title: __('Sell things online (eCommerce)'),
            description: __(
                'Set up an online store where people can make payments for products and services.'
            ),
            image: `recommendation/${languageCode}/category-01-store.jpg`,
            recommendations: ['Square Online Store', 'Shopify', 'Magento'],
        },

        {
            id: 'promotion',
            title: __('Promote my business'),
            description: __(
                'Communicate your business offering and let clients know how to get in touch.'
            ),
            image: `recommendation/${languageCode}/category-02-business.jpg`,
            recommendations: ['Wix', 'Squarespace', 'Weebly'],
        },

        {
            id: 'blog',
            title: __('Share ideas (Blog)'),
            description: __(
                'Create an online journal with topical information that you can regularly update.'
            ),
            image: `recommendation/${languageCode}/category-03-blog.jpg`,
            recommendations: ['Wix', 'Squarespace', 'Jimdo'],
        },

        {
            id: 'portfolio',
            title: __('Showcase work (Portfolio)'),
            description: __(
                'Display examples of previous work or create a visual CV to impress future employers.'
            ),
            image: `recommendation/${languageCode}/category-04-portfolio.jpg`,
            recommendations: ['Wix', 'Squarespace', 'Weebly'],
        },

        {
            id: 'educational',
            title: __('Learning material (Educational)'),
            description: __(
                'Build an online presence for an educational institution with courses, downloadable files, and subscription functions.'
            ),
            image: `recommendation/${languageCode}/category-05-education.jpg`,
            recommendations: ['Squarespace', 'Wix', 'Weebly'],
        },

        {
            id: 'other',
            title: __('Something else?'),
            description: __(
                'Get anything created, from a single landing page to a website directory or an online collaboration space.'
            ),
            image: `recommendation/${languageCode}/category-06-other.jpg`,
            recommendations: ['Squarespace', 'Wix', 'Weebly'],
        },
    ];
}

interface Props {
    languageCode: string;
}

function RecommendationSection({ languageCode }: Props) {
    const categories = getCategories(languageCode);

    let introduction = __(
        'Consider the goal of your website. Do you need a blog? A photo gallery? Or a place to sell your products online?'
    );

    if (languageCode === 'fr') {
        introduction = `Il existe plusieurs services de création de site web à travers lesquels vous pouvez mettre en place les bases de votre site. Chacun a ses spécificités. Nous vous conseillons de décider en fonction des objectifs de votre site web. Vous avez besoin d'un blog pro ? D'une galerie d'images ? Ou d'un emplacement pour vendre vos produits en ligne ?
`;
    }

    return (
        <>
            <Heading type="h2" variations={['h2', 'with-divider']}>
                {__('To start, what is your website for?')}
            </Heading>
            <Box paddingLeft={['none', 'gutter']}>
                <Paragraph variations={['narrow-widescreen']}>{introduction}</Paragraph>
            </Box>
            <Columns rowGap={['space10', 'space10', 'space14', 'space14']}>
                {categories.map((category) => (
                    <Column key={category.id} width={[12, 6, 4, 4]}>
                        <Category
                            id={category.id}
                            image={publicImageUrl(`${imageFolder}/${category.image}`)}
                            description={category.description}
                            recommendations={category.recommendations}
                            title={category.title}
                        />
                    </Column>
                ))}
            </Columns>
            <Box
                style={{ color: colors.heritage }}
                display="flex"
                justifyContent="center"
                marginTop="space12"
                marginBottom="space12"
            >
                <CtaPair
                    variations={['chameleon', 'responsive', 'jumbo', 'no-margin']}
                    primary={{
                        label: __('Get started'),
                        url: webOnboarderGetStartedPath(),
                        variations: ['jumbo', 'primary', 'chameleon', 'responsive'],
                    }}
                    secondary={
                        <Button
                            href="#compare"
                            variations={['jumbo', 'marketing-secondary', 'chameleon', 'responsive']}
                        >
                            {__('Compare options')}
                        </Button>
                    }
                />
            </Box>
        </>
    );
}

export default RecommendationSection;
